// utils/worker/fallbackPolling.js

import secureFetch from './fetch';
import { trackerHandler } from './tracker';

// 模拟 Worker 的 timers 和 lastResults
const timers = new Map(); // key => intervalId
const lastResults = new Map(); // key => lastResult

// 与 Worker 版本相同的比较策略
import { getCompareStrategies } from './compareStrategies'; // 

function shouldSendResult(key, newResult, url) {
  const lastResult = lastResults.get(key);
  if (!lastResult) {
    lastResults.set(key, JSON.parse(JSON.stringify(newResult)));
    return true;
  }

  const needCompare = ['code', 'message', 'err', 'data'];
  for (const field of needCompare) {
    if (field === 'data') {
      const strategy = getCompareStrategies(url);
      const newResultStr = strategy(lastResult, newResult);
      if (newResultStr) {
        lastResults.set(key, JSON.parse(newResultStr));
        return true;
      }
    } else if (lastResult[field] !== newResult[field]) {
      lastResults.set(key, JSON.parse(JSON.stringify(newResult)));
      return true;
    }
  }
  return false;
}

export function startPolling(key, config, notify) {
  // 如果未登录，直接不往下执行
  if(!config.token) return;
  // 防止重复启动
  if (timers.has(key)) {
    clearInterval(timers.get(key));
  }

  lastResults.delete(key);

  const fn = async () => {
    try {
      trackerHandler({ key, event: 'request_start', payload: config });
      const result = await secureFetch(config.url, config);
      trackerHandler({ key, event: 'response_success', payload: config, result });

      if (shouldSendResult(key, result, config.url)) {
        notify({ status: 'success', key, result });
      }
    } catch (error) {
      trackerHandler({ key, event: 'response_error', payload: config });
      let errorMessage = '请求失败';
      if (error.name === 'AbortError' || error.message.includes('aborted')) {
        errorMessage = config.timeout ? `请求超时（${config.timeout}ms）` : '请求被中断';
      } else if (error.message.includes('Network')) {
        errorMessage = '网络错误，请检查连接';
      } else if (error.message.includes('HTTP')) {
        errorMessage = `服务器错误: ${error.message}`;
      } else {
        errorMessage = error.message || '未知错误';
      }
      notify({ status: 'error', key, error: {
        errorMessage,
        name: error.name,
        message: error.message,
        stack: error.stack
      }});
    }
  };

  fn(); // 立即执行一次
  const intervalId = setInterval(fn, config.interval);
  timers.set(key, intervalId);
}

export function stopPolling(key) {
  const id = timers.get(key);
  if (id) {
    clearInterval(id);
    timers.delete(key);
    lastResults.delete(key);
  }
}

// 版本检查降级（简单实现）
const versionCheckTimers = new Map();

export function startVersionCheck(taskId, config, notify) {
  if (versionCheckTimers.has(taskId)) {
    clearInterval(versionCheckTimers.get(taskId));
  }

  const fn = async () => {
    try {
      if (!config.url || !config.version) return;
      const res = await fetch(config.url, { cache: 'no-store' }).then(r => r.json());
      if (res.version && config.version !== res.version) {
        notify({ taskId, newVersion: res.version, oldVersion: config.version });
        clearInterval(versionCheckTimers.get(taskId));
        versionCheckTimers.delete(taskId);
      }
    } catch (e) {
      // silent
    }
  };

  fn();
  const id = setInterval(fn, config.interval || 10000);
  versionCheckTimers.set(taskId, id);
}

export function stopVersionCheck(taskId) {
  const id = versionCheckTimers.get(taskId);
  if (id) {
    clearInterval(id);
    versionCheckTimers.delete(taskId);
  }
}

export default  {
  startPolling,
  stopPolling,
  startVersionCheck,
  stopVersionCheck
}